import Controller from '@ember/controller';
import { reads } from '@ember/object/computed';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { dateToken } from '@qonto/ui-kit/utils/date-token';
import window from 'ember-window-mock';

import { companyCreationJsURL, jsURL, registerJsURL } from 'register-partners-js/constants/hosts';
import { ContentWrapper } from 'register-partners-js/react/components/content-wrapper';
import { MainLayout } from 'register-partners-js/react/components/layouts/main';

export default class RegistrationsController extends Controller {
  mainLayout = MainLayout;

  @service currentUser;
  @service deviceManager;
  @service intl;
  @service router;
  @service segment;

  @reads('currentUser.user') user;

  @tracked clickedOrganization = null;

  contentWrapper = ContentWrapper;

  get userFirstName() {
    return this.user?.firstName ?? this.user?.email;
  }

  get ongoingRegistrations() {
    return [
      ...this.ongoingPartnerRegistrations,
      ...this.ongoingRegisterRegistrations,
      ...this.ongoingCapitalDepositRegistrations,
      ...this.ongoingCompanyCreationFlow,
    ];
  }

  get ongoingPartnerRegistrations() {
    let { pendingPartnerRegistrations, partnerRegistrationsViaRegister } = this.model;

    // Normalize both APIs into a common shape
    let allPartnerRegistrations = [
      ...pendingPartnerRegistrations.map(org => ({
        organization: org,
        registrationId: org.registration.id,
        legalFlow: org.registration.legalFlow,
        partnerName: org.registration.partnerName,
      })),
      ...partnerRegistrationsViaRegister.map(org => ({
        organization: org,
        registrationId: org.partnerRegistration.get('id'),
        legalFlow: org.partnerRegistration.get('legalFlow'),
        partnerName: org.partnerRegistration.get('partnerName'),
      })),
    ];

    if (!allPartnerRegistrations.length) return [];

    return allPartnerRegistrations.map(
      ({ organization, registrationId, legalFlow, partnerName }) => {
        let isCompanyCreation = legalFlow === 'company_creation';
        let date = dateToken({ date: organization.createdAt, locale: this.locale });
        let onClick = () => this.resumePartnerRegistration(organization, registrationId);
        let companyName = organization.legalName ?? organization.name;

        return {
          title: isCompanyCreation
            ? this.intl.t('resume-screen.company-creation-partner-selector.title', {
                companyName,
                partnerName,
              })
            : this.intl.t('resume-screen.existing-business-selector.title', {
                companyName,
              }),
          subtitle: isCompanyCreation
            ? this.intl.t('resume-screen.company-creation-partner-selector.subtitle', { date })
            : this.intl.t('resume-screen.existing-business-selector.subtitle', { date }),
          onClick,
          pending: this.clickedOrganization === organization.id,
        };
      }
    );
  }

  get ongoingRegisterRegistrations() {
    return this.model.registerOrganizationsWithPayment.map(organization => {
      let isExistingBusiness = organization.underRegistration === false;
      let date = dateToken({ date: organization.createdAt, locale: this.locale });
      let onClick = () => this.resumeRegisterRegistration(organization);

      return {
        title: isExistingBusiness
          ? this.intl.t('resume-screen.existing-business-selector.title', {
              companyName: organization.name,
            })
          : this.intl.t('resume-screen.company-creation-selector.title', {
              companyName: organization.name,
            }),
        subtitle: isExistingBusiness
          ? this.intl.t('resume-screen.existing-business-selector.subtitle', {
              date,
            })
          : this.intl.t('resume-screen.company-creation-selector.subtitle', {
              date,
            }),
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get ongoingCompanyCreationFlow() {
    return this.model.ccOrganizations.map(organization => {
      let date = dateToken({ date: organization.attributes.created_at, locale: this.locale });
      let onClick = () => {
        window.location.href = `${companyCreationJsURL}?organization=${organization.id}`;
      };

      return {
        title: this.intl.t('resume-screen.company-creation-selector.title', {
          companyName: organization.attributes.name,
        }),
        subtitle: this.intl.t('resume-screen.company-creation-selector.subtitle', {
          date,
        }),
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get ongoingCapitalDepositRegistrations() {
    return this.activeAppOrganizationsInCapitalDepositState.map(organization => {
      let date = dateToken({ date: organization.createdAt, locale: this.locale });
      let onClick = () => this.redirectToApp(organization);

      return {
        title: this.intl.t('resume-screen.company-creation-selector.title', {
          companyName: organization.name,
        }),
        subtitle: this.intl.t('resume-screen.company-creation-partner-selector.subtitle-kdeposit', {
          date,
        }),
        isOngoingCapitalDeposit: true,
        onClick,
        pending: this.clickedOrganization === organization.id,
      };
    });
  }

  get activeAppOrganizationsInCapitalDepositState() {
    return this.model.activeAppOrganizations.filter(this.isOngoingCapitalDeposit);
  }

  get locale() {
    return this.intl.primaryLocale;
  }

  isOngoingCapitalDeposit(organization) {
    return organization.underRegistration;
  }

  resumePartnerRegistration(organization, registrationId) {
    this.clickedOrganization = organization.id;
    this.segment.track('onboarding_partner_register_accounts_launcher_resume', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });
    this.router.replaceWith('registration', registrationId);
  }

  resumeRegisterRegistration(organization) {
    this.clickedOrganization = organization.id;
    this.segment.track('onboarding_partner_register_accounts_launcher_resume', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
    });

    window.location.href = `${registerJsURL}/organizations/${organization.id}/deposit-capital/payment-success`;
  }

  redirectToApp(organization) {
    let { id, slug, underRegistration } = organization;
    this.clickedOrganization = id;

    this.segment.track('onboarding_accounts_launcher_select', {
      is_mobile_browser: this.deviceManager.isMobile,
      is_webview: this.deviceManager.isWebview,
      organization_id: id,
      under_registration: underRegistration,
    });

    if (this.deviceManager.isMobile || this.deviceManager.isWebview) {
      return this.router.transitionTo('registrations.already-created', id);
    }

    window.location.replace(`${jsURL}/organizations/${slug}`);
  }
}
